
import React from 'react';
import { X, Moon, Sun, FlaskConical, Monitor, ArrowUp, ArrowDown, List, Image as ImageIcon } from 'lucide-react';

type CoverImageSize = 'small' | 'medium' | 'large';

interface SettingsModalProps {
  onClose: () => void;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  isExperimental: boolean;
  toggleExperimental: () => void;
  categoryOrder?: string[];
  onUpdateCategoryOrder?: (newOrder: string[]) => void;
  coverImageSize?: CoverImageSize;
  onChangeCoverImageSize?: (size: CoverImageSize) => void;
}

const COVER_SIZE_OPTIONS: { id: CoverImageSize; label: string }[] = [
  { id: 'small', label: 'Klein' },
  { id: 'medium', label: 'Mittel' },
  { id: 'large', label: 'Groß' }
];

export const SettingsModal: React.FC<SettingsModalProps> = ({
  onClose,
  isDarkMode,
  toggleDarkMode,
  isExperimental,
  toggleExperimental,
  categoryOrder,
  onUpdateCategoryOrder,
  coverImageSize,
  onChangeCoverImageSize
}) => {

  const moveCategory = (index: number, direction: 'up' | 'down') => {
    if (!categoryOrder || !onUpdateCategoryOrder) return;
    
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === categoryOrder.length - 1) return;

    const newOrder = [...categoryOrder];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newOrder[index], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[index]];
    onUpdateCategoryOrder(newOrder);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in zoom-in-95 duration-200">
      <div className={`${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border flex flex-col relative max-h-[85vh]`}>
        
        <div className={`p-5 border-b ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-100 bg-white'} flex justify-between items-center`}>
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Einstellungen</h2>
            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Darstellung & Funktionen</p>
          </div>
          <button 
            onClick={onClose} 
            className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className={`p-6 space-y-6 ${isDarkMode ? 'text-slate-200' : 'text-slate-700'} overflow-y-auto`}>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-50 text-indigo-600'}`}>
                {isDarkMode ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </div>
              <div>
                <h3 className="font-bold text-sm">Erscheinungsbild</h3>
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {isDarkMode ? 'Dunkler Modus aktiv' : 'Heller Modus aktiv'}
                </p>
              </div>
            </div>
            
            <button 
              onClick={toggleDarkMode}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${isDarkMode ? 'bg-indigo-600' : 'bg-slate-200'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isDarkMode ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <div className={`h-px w-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}></div>

          {categoryOrder && onUpdateCategoryOrder && (
            <>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-600'}`}>
                    <List className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm">Reihenfolge der Themen</h3>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Sortierung im PDF Maßnahmenkatalog
                    </p>
                  </div>
                </div>

                <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-slate-200 bg-slate-50'}`}>
                  {categoryOrder.map((cat, idx) => (
                    <div 
                      key={cat} 
                      className={`flex items-center justify-between p-2.5 px-4 text-sm font-medium border-b last:border-0 ${isDarkMode ? 'border-slate-700 text-slate-300' : 'border-slate-200 text-slate-700'}`}
                    >
                      <span>{cat}</span>
                      <div className="flex gap-1">
                        <button 
                          onClick={() => moveCategory(idx, 'up')}
                          disabled={idx === 0}
                          className={`p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 ${idx === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => moveCategory(idx, 'down')}
                          disabled={idx === categoryOrder.length - 1}
                          className={`p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 ${idx === categoryOrder.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={`h-px w-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}></div>
            </>
          )}

          {coverImageSize && onChangeCoverImageSize && (
            <>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-emerald-900 text-emerald-300' : 'bg-emerald-50 text-emerald-600'}`}>
                    <ImageIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm">Deckblatt-Bild</h3>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Größe des Projektbilds auf dem Deckblatt (Vorschau & PDF)
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {COVER_SIZE_OPTIONS.map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => onChangeCoverImageSize(opt.id)}
                      className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold border transition-colors ${
                        coverImageSize === opt.id
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : isDarkMode
                            ? 'bg-slate-900/50 text-slate-300 border-slate-700 hover:bg-slate-700'
                            : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={`h-px w-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}></div>
            </>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${isExperimental ? 'bg-purple-900 text-purple-300' : 'bg-purple-50 text-purple-600'}`}>
                <FlaskConical className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Experimentelle Funktionen</h3>
                <div className="flex flex-col gap-1 mt-1">
                   <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                     Querformat für Vorschau & PDF-Export
                   </p>
                   {isExperimental && (
                     <div className="flex gap-2">
                        <span className="text-[11px] flex items-center gap-1 font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                           <Monitor className="w-3 h-3" /> Querformat
                        </span>
                     </div>
                   )}
                </div>
              </div>
            </div>
            
            <button 
              onClick={toggleExperimental}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${isExperimental ? 'bg-purple-600' : 'bg-slate-200'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isExperimental ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          
          <div className={`text-xs p-3 rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-400' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
            <strong>Hinweis:</strong> Experimentelle Funktionen sind möglicherweise nicht vollständig für alle Browser optimiert.
          </div>

        </div>

      </div>
    </div>
  );
};
